import { MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import supabase from 'modules/core/supabase/supabase';
import { TEMPLATE_RESOLUTIONS, resolveTemplateResolution } from 'modules/manager/domain/template-resolutions';
import { useCallback, useMemo, useState } from 'react';
import { UDText } from 'ud-ui/components';
import Modal from 'ud-ui/components/modal';
import MediaManagerTemplateForm, {
  DEFAULT_RESOLUTION_OPTION,
  DEFAULT_TEMPLATE_TYPE_OPTION,
  MediaManagerTemplateFormValues,
  buildInitialSubtype,
  getResolutionOption,
  getTemplateTypeOption,
} from '../../components/forms/media-manager-template-form';

interface Args {
  templates: MediaManagerTemplate[];
  onUpdated: () => void;
}

function useEditTemplateModal({ templates, onUpdated }: Args) {
  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const open = useCallback((next: MediaManagerTemplate) => {
    setTemplate(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  const existingNames = useMemo(
    () => templates.map((t) => t.name.trim()).filter((name) => name !== template?.name),
    [templates, template],
  );

  const onSubmit = useCallback(
    async (values: MediaManagerTemplateFormValues) => {
      if (!template) return;
      setIsLoading(true);
      try {
        const resolution = TEMPLATE_RESOLUTIONS[values.resolution.value];
        await supabase.mediaManager.updateTemplate({
          id: template.id,
          name: values.name.trim(),
          width: resolution.width,
          height: resolution.height,
          templateType: values.templateType.value,
          templateSubtypes: values.subtype ? [values.subtype.value] : [],
        });
        onUpdated();
        close();
      } catch (error) {
        console.error('Failed to update media manager template:', error);
      } finally {
        setIsLoading(false);
      }
    },
    [template, onUpdated, close],
  );

  return {
    open,
    close,
    modal: (
      <Modal show={isOpen} onClose={close}>
        <UDText type="title-3" className="mb-5">
          Редактировать шаблон
        </UDText>
        <MediaManagerTemplateForm
          initialValues={{
            name: template?.name ?? '',
            resolution: template
              ? getResolutionOption(resolveTemplateResolution(template.width, template.height).key)
              : DEFAULT_RESOLUTION_OPTION,
            templateType: template ? getTemplateTypeOption(template.templateType) : DEFAULT_TEMPLATE_TYPE_OPTION,
            subtype: template ? buildInitialSubtype(template.templateType, template.templateSubtypes) : null,
          }}
          existingNames={existingNames}
          isLoading={isLoading}
          submitLabel="Сохранить"
          onSubmit={onSubmit}
          enableReinitialize
        />
      </Modal>
    ),
  };
}

export default useEditTemplateModal;
